/**
 * Partitions Folder Loader - Loads child partitions when a table's Partitions
 * folder is expanded (PostgreSQL declarative-partitioned parents only).
 */

import {
  DatabaseExplorerNode,
  MessageTreeNode,
  PartitionsFolderNode,
  createPartitionNode,
  PartitionNodeMetadata,
} from '../../explorer/models/DatabaseTreeModels';
import {
  createTableSubfolderEmptyNode,
  createTableSubfolderErrorNode,
} from './objectFolderUtils';
import { getPartitions, PartitionInfo, formatPartitionDisplayName } from './partitionsService';

export interface LoadPartitionsFolderOptions {
  node: PartitionsFolderNode;
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void;
  forceReload?: boolean;
}

/**
 * Loads the child partitions for a table when the Partitions folder is expanded.
 */
export async function loadPartitionsFolder({
  node,
  updateNode,
  forceReload = false,
}: LoadPartitionsFolderOptions) {
  if (!forceReload && node.children && node.children.length > 0) {
    const firstChild = node.children[0];
    if (firstChild && !['message', 'error'].includes(firstChild.type)) {
      return;
    }
  }

  // Partitions are a PostgreSQL-only concept — record the expansion (fire-and-forget).
  window.electronAPI?.analytics
    ?.track('feature:partition_folder_expand', { engine: 'pgsql' })
    .catch(() => {});

  const { connectionId, database, schema, tableName } = node.metadata;

  if (!database) {
    updateNode(node.id, {
      children: [
        createTableSubfolderErrorNode(
          connectionId, '', schema, tableName, 'partitions-folder', node.id, 'Database not specified'
        ),
      ],
      isLoading: false,
    });
    return;
  }

  updateNode(node.id, {
    children: [
      {
        id: `loading-${connectionId}-${database}-${schema}-${tableName}-partitions`,
        parentId: node.id,
        name: 'Loading partitions...',
        type: 'message',
        canHaveChildren: false,
        children: [],
        isExpanded: false,
        icon: 'loading',
        metadata: { connectionId, database },
      } as MessageTreeNode,
    ],
    isLoading: true,
  });

  try {
    const result = await getPartitions(connectionId, database, schema, tableName);

    if (result.error) {
      updateNode(node.id, {
        children: [
          createTableSubfolderErrorNode(
            connectionId, database, schema, tableName, 'partitions-folder', node.id, result.error
          ),
        ],
        isLoading: false,
      });
      return;
    }

    if (result.partitions.length === 0) {
      updateNode(node.id, {
        children: [
          createTableSubfolderEmptyNode(
            connectionId, database, schema, tableName, 'partitions-folder', node.id
          ),
        ],
        isLoading: false,
      });
      return;
    }

    const partitionNodes = result.partitions.map((partition: PartitionInfo) => {
      const metadata: PartitionNodeMetadata = {
        connectionId,
        database,
        schema,
        tableName,
        partitionSchema: partition.schema,
        partitionName: partition.name,
        bound: partition.bound,
      };
      const partitionNode = createPartitionNode(metadata, node.id);
      partitionNode.name = formatPartitionDisplayName(partition);
      return partitionNode;
    });

    updateNode(node.id, {
      children: partitionNodes,
      isLoading: false,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load partitions';
    updateNode(node.id, {
      children: [
        createTableSubfolderErrorNode(
          connectionId, database, schema, tableName, 'partitions-folder', node.id, message
        ),
      ],
      isLoading: false,
    });
  }
}
